import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import ReactGA from 'react-ga4';

import App from '@app/App';
import store from '@store/store';

import './utils/i18n';
import './index.scss';

const { VITE_NODE_ENV, VITE_GA_ID } = import.meta.env;

if (VITE_NODE_ENV === 'production' && VITE_GA_ID) {
  ReactGA.initialize(VITE_GA_ID);
}

const rootElement = document.getElementById('root') as HTMLElement | null;

if (!rootElement) {
  throw new Error(
    'Could not find <div id="root"> in index.html.'
  );
}

// Track whether React has successfully mounted.
// We only show the fatal error overlay for errors that happen
// BEFORE React takes ownership of the DOM (module init, Firebase
// config missing, etc.).  Once React is mounted, React's own
// error boundaries handle in-app errors — we must NOT touch the
// DOM directly or we trigger the removeChild clash.
let reactMounted = false;

function renderFatalError(error: unknown) {
  // Never interfere with the DOM once React has mounted.
  if (reactMounted) return;

  const message = error instanceof Error ? error.message : String(error);

  // Replace the entire body rather than innerHTML of #root so we
  // can't accidentally trigger React's removeChild reconciliation.
  document.body.innerHTML = `
    <div style="
      font-family: sans-serif;
      padding: 24px;
      max-width: 640px;
      margin: 40px auto;
      color: #b91c1c;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
    ">
      <h2 style="margin-top:0;">App failed to start</h2>
      <p style="white-space: pre-wrap; word-break: break-word;">${message}</p>
      <button onclick="window.location.reload()" style="
        padding: 8px 16px;
        cursor: pointer;
      ">Reload</button>
    </div>
  `;
}

// Only catch errors that happen before React mounts.
// After mounting, remove these listeners so they don't
// interfere with React's own error handling.
function onUncaughtError(event: ErrorEvent) {
  if (reactMounted) return;
  console.error('Uncaught error before/while mounting app:', event.error || event.message);
  renderFatalError(event.error || event.message);
}

function onUnhandledRejection(event: PromiseRejectionEvent) {
  if (reactMounted) return;
  console.error('Unhandled promise rejection:', event.reason);
  renderFatalError(event.reason);
}

window.addEventListener('error', onUncaughtError);
window.addEventListener('unhandledrejection', onUnhandledRejection);

try {
  const root = createRoot(rootElement);

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <HashRouter>
          <App />
        </HashRouter>
      </Provider>
    </React.StrictMode>
  );

  // Mark React as mounted so our global error listeners
  // stand down and stop touching the DOM.
  reactMounted = true;

  // Clean up the pre-mount error listeners — React and its
  // error boundaries take over from here.
  window.removeEventListener('error', onUncaughtError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);

} catch (error) {
  console.error('Failed to render app:', error);
  renderFatalError(error);
}